import { buildManifestAssets, type ThemeAssetRow, type ThemeManifest } from "@yjsgames/theme";
import { COMBAT, GRID } from "../../data/balance.js";
import themeAssetRows from "../../data/generated/themeAssets_minimal.json";
import {
  THEME_IMAGE_KEYS,
  THEME_PROCEDURAL_KEYS,
  type ThemeImageKey,
  type ThemeProceduralKey,
} from "../imageKeys.js";
import type { PaletteToken } from "../paletteTokens.js";

// public 기준 상대 경로 — 루트 "/" 금지(서브경로 배포 404). themeAssets_minimal.tsv도 동일 규칙.
const BASE = "themes/minimal";

/**
 * minimal 테마 매니페스트 — 2호(미니멀라이즈 추상화). 컨셉 정의 = docs/art-concept-minimal-abstract.md.
 * 기하 원형 + 플랫 단색 + 뉴트럴 다크 표면 + 액센트 2색(앰버/시안). 에셋은 tools/generate-theme-minimal.mjs로
 * 절차 생성(수정 = 스크립트 재실행). 선택: `VITE_APP_THEME=minimal` 빌드/dev — git 롤백 없이 테마 전환.
 * 에셋 SSoT = data/themeAssets_minimal.tsv (키 완결성은 buildManifestAssets 로드 throw).
 */
const assets = buildManifestAssets<ThemeImageKey, ThemeProceduralKey>(
  themeAssetRows as readonly ThemeAssetRow[],
  {
    imageKeys: THEME_IMAGE_KEYS,
    proceduralKeys: THEME_PROCEDURAL_KEYS,
    proceduralSizeOverrides: {
      // 벽돌/공 크기는 balance SSoT — fantasy와 동일 주입 (테마는 색·radius만 소유).
      brick: { width: GRID.brickWidth, height: GRID.brickHeight },
      brick_gold: { width: GRID.brickWidth, height: GRID.brickHeight },
      brick_mystery: { width: GRID.brickWidth, height: GRID.brickHeight },
      ball: { radius: COMBAT.ballRadius },
    },
  },
);

const manifest = {
  name: "minimal",
  images: assets.images,
  procedural: assets.procedural,
  palette: {
    // 전역/표면 — 채도 0 뉴트럴 다크 (fantasy의 보라끼 제거)
    appBackground: "#101014",
    overlayDim: "#08080c",
    adAreaBg: "#000000",
    chipBg: "#24242c",
    borderSoft: "#2a2a33",
    panelDivider: "#2e2e38",
    // 텍스트 — 웜그레이 스케일
    textBright: "#ffffff",
    textPrimary: "#e8e8ea",
    textSecondary: "#c9c9d2",
    textSoft: "#d8d8de",
    textSoftDim: "#b4b4be",
    textDim: "#9a9aa2",
    textMuted: "#84848e",
    textFaint: "#74747e",
    textLocked: "#54545e",
    textDisabled: "#64646e",
    textPlaceholder: "#6a6a74",
    textOutline: "#000000",
    hudText: "#e8e8ea",
    // 버튼/위젯 — 명도 단계로만 상태 구분
    buttonBg: "#24242c",
    buttonBorder: "#3a3a44",
    buttonActiveBg: "#3a3a46",
    buttonPressedBg: "#2e2e38",
    surfaceDisabled: "#16161c",
    cellBg: "#1a1a20",
    cellStroke: "#2e2e38",
    selectionStroke: "#ffffff",
    scrollBar: "#54545e",
    scrollTrack: "#16161c",
    sliderFill: "#5ad7e6",
    sliderTrack: "#24242c",
    // 액센트/신호 — 앰버(재화·강조) + 채도 낮춘 신호색
    accentGold: "#ffb84d",
    accentAmber: "#ffc66b",
    accentGoldDim: "#b08a48",
    damageText: "#ffe2a8",
    positive: "#8fd98a",
    positiveSoft: "#a8dba2",
    infoBlue: "#5ad7e6",
    signalDanger: "#e05d57",
    // 게임뷰 (조준선/발사체) — 시안 계열 통일 (네온 대비 ↑)
    aimCore: "#6ff2ff",
    aimGlow: "#49b9c9",
    projectileNeutral: "#ffffff",
    projectileFire: "#ff8a4d",
    projectileElectric: "#6ee8f2",
    projectileIce: "#8ac2ef",
    // 모듈 그리드 — 잠금/빈칸은 후퇴, 경로·포트만 네온 시안 강조 (dark neon 대비 — 가독 지시 ⑥⑦)
    gridCellOnPath: "#16242e",
    gridCellEmpty: "#0c0c10",
    gridCellFilled: "#1a1a20",
    gridCellEmptyStroke: "#1a1a22",
    gridCellStroke: "#2a2a33",
    gridPathGlow: "#6ff2ff",
    portOn: "#8ff4ff",
    portOff: "#64646e",
    portBgOn: "#1e2e34",
    portBg: "#16161c",
    // 물만 흐르는(미발동) — F에 안 닿는 막다른 곁가지·미연결. 발동(시안)과 명확히 구분되는 amber.
    gridCellWater: "#2e2417",
    gridPathGlowWater: "#ffae5c",
    portWater: "#ffc27a",
    portBgWater: "#33281b",
    gridCellBlocked: "#2e1717",
    gridPathGlowBlocked: "#ff5c5c",
    portBlocked: "#ff8585",
    portBgBlocked: "#331b1b",
    gridCellSynergy: "#241a38",
    gridPathGlowSynergy: "#b478f5",
    endpointStart: "#8fd98a",
    endpointFire: "#ff8a4d",
  },
  font: {
    family: "PretendardSemiBold",
    file: `${BASE}/fonts/Pretendard-SemiBold.otf`,
    weight: "600",
  },
  sounds: { basePath: `${BASE}/sounds/` },
} satisfies ThemeManifest<ThemeImageKey, PaletteToken, ThemeProceduralKey>;

export default manifest;
